import PropTypes from 'prop-types';
import { useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Unstable_Grid2';
import { Button, IconButton, ListItemText, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { GridCloseIcon } from '@mui/x-data-grid';
import { useSnackbar } from 'src/components/snackbar';
import Iconify from 'src/components/iconify/iconify';
import { fCurrency } from 'src/utils/format-number';
import axiosInstance, { API_ENDPOINTS } from 'src/utils/axios';

// ----------------------------------------------------------------------

const emptyForm = {
  portugueseName: '',
  frenchName: '',
  englishName: '',
  portugueseDescription: '',
  frenchDescription: '',
  englishDescription: '',
  cost: '',
  allergens: '',
};

export default function ManualDailyDishTab({ menuId, dayOfWeek, dayLabel, dishes, refresh }) {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  const dayDishes = (dishes || [])
    .filter((d) => d.dayOfWeek === dayOfWeek)
    .sort((a, b) => a.displayOrder - b.displayOrder);

  const handleChange = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  // Traduzir PT -> FR/EN via /api/translate (MyMemory)
  const handleTranslate = async () => {
    if (!form.portugueseName?.trim()) {
      enqueueSnackbar('Preencha o nome em português primeiro.', { variant: 'warning' });
      return;
    }
    setTranslating(true);
    try {
      const translate = async (text, to) => {
        if (!text?.trim()) return '';
        const res = await axiosInstance.post(API_ENDPOINTS.translate.post, {
          text,
          from: 'pt',
          to,
        });
        return res.data?.success ? res.data.translatedText : '';
      };

      const [fr, en, frDesc, enDesc] = await Promise.all([
        translate(form.portugueseName, 'fr'),
        translate(form.portugueseName, 'en'),
        translate(form.portugueseDescription, 'fr'),
        translate(form.portugueseDescription, 'en'),
      ]);

      setForm((prev) => ({
        ...prev,
        frenchName: fr || prev.frenchName,
        englishName: en || prev.englishName,
        frenchDescription: frDesc || prev.frenchDescription,
        englishDescription: enDesc || prev.englishDescription,
      }));
      enqueueSnackbar('Traduzido. Reveja antes de guardar.', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Não foi possível traduzir. Preencha à mão.', { variant: 'error' });
    } finally {
      setTranslating(false);
    }
  };

  const handleSave = async () => {
    if (!form.portugueseName?.trim()) {
      enqueueSnackbar('O nome em português é obrigatório.', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const body = {
        dayOfWeek,
        portugueseName: form.portugueseName,
        frenchName: form.frenchName,
        englishName: form.englishName,
        portugueseDescription: form.portugueseDescription,
        frenchDescription: form.frenchDescription,
        englishDescription: form.englishDescription,
        cost: Number(form.cost) || 0,
        allergens: form.allergens,
      };

      const res = editingId
        ? await axiosInstance.put(`${API_ENDPOINTS.menu.manual_dish}/${menuId}/manual-dish/${editingId}`, body)
        : await axiosInstance.post(`${API_ENDPOINTS.menu.manual_dish}/${menuId}/manual-dish`, body);

      enqueueSnackbar(res.data.message, { variant: res.data.success ? 'success' : 'error' });
      if (res.data.success) {
        await refresh();
        resetForm();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erro ao guardar o prato manual.', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (dish) => {
    setEditingId(dish.id);
    setForm({
      portugueseName: dish.portugueseName || '',
      frenchName: dish.frenchName || '',
      englishName: dish.englishName || '',
      portugueseDescription: dish.portugueseDescription || '',
      frenchDescription: dish.frenchDescription || '',
      englishDescription: dish.englishDescription || '',
      cost: dish.cost ?? '',
      allergens: dish.allergens || '',
    });
  };

  const handleDelete = async (dish) => {
    try {
      const res = await axiosInstance.delete(`${API_ENDPOINTS.menu.manual_dish}/${menuId}/manual-dish/${dish.id}`);
      enqueueSnackbar(res.data.message, { variant: res.data.success ? 'success' : 'error' });
      if (res.data.success) {
        await refresh();
        if (editingId === dish.id) resetForm();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erro ao eliminar.', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.neutral' }}>
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {editingId ? 'Editar prato' : 'Novo prato'} — {dayLabel} (escrito à mão)
        </Typography>

        <Box
          rowGap={2}
          columnGap={2}
          display="grid"
          gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }}
        >
          <TextField label="Nome (Português)" value={form.portugueseName} onChange={handleChange('portugueseName')} required />
          <TextField label="Nome (Francês)" value={form.frenchName} onChange={handleChange('frenchName')} />
          <TextField label="Nome (Inglês)" value={form.englishName} onChange={handleChange('englishName')} />
          <TextField label="Descrição (PT)" value={form.portugueseDescription} onChange={handleChange('portugueseDescription')} multiline />
          <TextField label="Descrição (FR)" value={form.frenchDescription} onChange={handleChange('frenchDescription')} multiline />
          <TextField label="Descrição (EN)" value={form.englishDescription} onChange={handleChange('englishDescription')} multiline />
          <TextField label="Custo (€)" type="number" value={form.cost} onChange={handleChange('cost')} />
          <TextField label="Alergénios (ex: 1.4.7)" value={form.allergens} onChange={handleChange('allergens')} />
        </Box>

        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <LoadingButton
            variant="outlined"
            color="info"
            onClick={handleTranslate}
            loading={translating}
            startIcon={<Iconify icon="solar:translation-2-bold" />}
          >
            Traduzir (PT → FR/EN)
          </LoadingButton>
          <Box sx={{ flexGrow: 1 }} />
          {editingId && (
            <Button variant="text" color="inherit" onClick={resetForm}>
              Cancelar edição
            </Button>
          )}
          <LoadingButton variant="contained" onClick={handleSave} loading={saving} startIcon={<Iconify icon="mingcute:add-line" />}>
            {editingId ? 'Atualizar' : 'Adicionar'}
          </LoadingButton>
        </Box>
      </Card>

      {dayDishes.length > 0 ? (
        dayDishes.map((dish) => (
          <Grid key={dish.id} xs={12} sx={{ display: 'inline-flex', alignItems: 'center', p: 2, width: '100%' }}>
            <ListItemText
              primary={dish.portugueseName}
              secondary={
                <Box component="span" sx={{ typography: 'body2', color: 'text.disabled' }}>
                  {[dish.frenchName, dish.englishName].filter(Boolean).join(' / ')}
                  {dish.allergens ? `  ·  (${dish.allergens})` : ''}
                </Box>
              }
            />
            <Box sx={{ pr: 2 }}>{fCurrency(dish.cost)} €</Box>
            <IconButton aria-label="edit" onClick={() => handleEdit(dish)} color="info">
              <Iconify icon="solar:pen-bold" />
            </IconButton>
            <IconButton aria-label="remove" onClick={() => handleDelete(dish)} color="error">
              <GridCloseIcon />
            </IconButton>
          </Grid>
        ))
      ) : (
        <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
          Nenhum prato escrito para {dayLabel}.
        </Typography>
      )}
    </Box>
  );
}

ManualDailyDishTab.propTypes = {
  menuId: PropTypes.string,
  dayOfWeek: PropTypes.number,
  dayLabel: PropTypes.string,
  dishes: PropTypes.array,
  refresh: PropTypes.func,
};
